import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Hook pour détecter la taille d'écran
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return windowSize;
};



const Hero: React.FC = () => {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Préchargement de l'image de fond avec support WebP
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageLoaded(true);
    };
    img.onerror = () => {
      // Fallback vers l'image JPEG optimisée
      const fallbackImg = new Image();
      fallbackImg.onload = () => {
        setImageLoaded(true);
      };
      fallbackImg.onerror = () => {
        // En cas d'erreur totale, on considère l'image comme "chargée"
        setImageLoaded(true);
      };
      fallbackImg.src = '/hero-bg-optimized.jpg';
    };
    
    // Essayer d'abord WebP, puis fallback vers JPEG optimisé
    img.src = '/hero-bg.webp';
  }, []);
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleGetStarted = () => {
    // Redirection vers la page d'inscription
    window.location.href = `${window.location.origin}/register`;
  };

  const handleViewDemo = () => {
    // Logique pour voir les tarifs
    scrollToSection('pricing');
  };



  return (
    <section 
      className="relative overflow-hidden min-h-screen flex items-center justify-center transition-all duration-1000 ease-out"
      style={{
          backgroundImage: imageLoaded 
            ? `linear-gradient(135deg, rgba(17, 24, 39, 0.7), rgba(17, 24, 39, 0.5)), url('/hero-bg.webp')`
            : 'none',
          backgroundColor: imageLoaded ? 'transparent' : 'linear-gradient(135deg, #FF7900, #1f2937)',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          transition: 'all 1s ease-in-out'
        }}
    >
      {/* Overlay avec gradient orange et gris */}
      <div className="absolute inset-0 opacity-80" style={{
        background: 'linear-gradient(45deg, rgba(255, 121, 0, 0.1), rgba(17, 24, 39, 0.3))'
      }}></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          {/* Contenu centré */}
          <motion.div 
            className="lg:w-1/2 text-center lg:text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.6, 
              ease: "easeOut" 
            }}
          >


            {/* Titre principal avec effet de gradient */}
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight mb-6 md:mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.2,
                ease: "easeOut"
              }}
              style={{
                color: 'white',
                textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                letterSpacing: '-0.02em'
              }}
            >
              <span>Le digital</span>
              <br />
              <span>à la carte</span>
              <br />
              <span>pour votre </span>
              <span 
                className="text-transparent bg-clip-text"
                style={{
                  backgroundImage: 'linear-gradient(135deg, #FF7900, #ff9533)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  textShadow: 'none'
                }}
              >
                restaurant
              </span>
            </motion.h1>

            {/* Sous-titre */}
            <motion.p 
              className="text-lg sm:text-xl md:text-2xl mb-8 md:mb-12 leading-relaxed max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.4,
                ease: "easeOut"
              }}
              style={{
                color: 'rgba(255, 255, 255, 0.95)',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                fontWeight: '400'
              }}
            >
              Transformez votre présence digitale avec notre solution tout-en-un alimentée par l'IA : 
              menu QR code, réservations automatisées, posts sur les réseaux sociaux et bien plus.
            </motion.p>

            {/* Boutons d'action */}
            <motion.div 
              className="flex flex-col items-center lg:items-start space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.6,
                ease: "easeOut"
              }}
            >
              {/* Conteneur des boutons avec espacement uniforme */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center w-full">
                <motion.button
                  whileHover={{ 
                    scale: !isMobile ? 1.05 : 1.02, 
                    y: !isMobile ? -3 : -2 
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleGetStarted}
                  className="group relative text-white px-9 py-5 rounded-full font-semibold text-lg transition-all duration-300 w-full sm:w-auto"
                  style={{
                    background: 'linear-gradient(135deg, #FF7900, #ff9533)',
                    boxShadow: '0 8px 30px rgba(255, 121, 0, 0.4)'
                  }}
                >
                  Commencer gratuitement
                </motion.button>
                
                <motion.button
                  whileHover={{ 
                    scale: !isMobile ? 1.05 : 1.02, 
                    y: !isMobile ? -3 : -2 
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleViewDemo}
                  className="group text-white px-8 py-4 rounded-full font-semibold text-lg border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-300 w-full sm:w-auto backdrop-blur-sm"
                  style={{
                    boxShadow: '0 4px 20px rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <span className="group-hover:text-orange-300 transition-colors duration-300">Voir les tarifs</span>
                </motion.button>
              </div>
              
              {/* Texte informatif avec espacement réduit */}
              <p className="text-sm text-white/80 text-center lg:text-left mt-2">
                Essai gratuit 30 jours • Aucune carte bancaire requise
              </p>
            </motion.div>
          </motion.div>



        </div>
      </div>
    </section>
  );
};

export default Hero;